import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Save, Loader2, Pencil, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GmailSettingsCard from "@/components/GmailSettingsCard";

export default function MyProfile() {
  const { user: authUser } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const isAdmin = authUser && ["admin", "super_admin"].includes(authUser.role);

  async function load() {
    try {
      const { data } = await api.get("/auth/profile");
      setProfile(data);
      setForm({
        name: data.name || "",
        email: data.email || "",
        employee_id: data.employee_id || "",
        department: data.department || "",
        designation: data.designation || "",
        mobile_number: data.mobile_number || "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load profile");
    }
  }

  useEffect(() => { load(); }, []);

  async function saveProfile() {
    setSaving(true);
    try {
      // Users can only edit email + mobile. Admin can edit everything.
      const payload = {
        email: form.email,
        mobile_number: form.mobile_number,
      };
      if (isAdmin) {
        payload.name = form.name;
        payload.employee_id = form.employee_id;
        payload.department = form.department;
        payload.designation = form.designation;
      }
      const { data } = await api.patch("/auth/profile", payload);
      setProfile(data);
      setEditing(false);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    setEditing(false);
    setForm({
      name: profile.name || "",
      email: profile.email || "",
      employee_id: profile.employee_id || "",
      department: profile.department || "",
      designation: profile.designation || "",
      mobile_number: profile.mobile_number || "",
    });
  }

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto py-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const roleLabel = ROLE_LABELS[profile.role] || profile.role;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="my-profile-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
            <User className="w-3.5 h-3.5" />
            User Account
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">My Profile</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? "As Administrator, you can edit all profile fields."
              : "You may edit your Email and Mobile Number. Contact Administrator for other changes."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="btn-change-password-shortcut"
            variant="outline"
            onClick={() => nav("/profile/change-password")}
          >
            Change Password
          </Button>
          {!editing && (
            <Button data-testid="btn-edit-profile" onClick={() => setEditing(true)}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-4 pb-4 mb-4 border-b border-slate-100">
          <div className="w-14 h-14 rounded-full bg-red-700 text-white flex items-center justify-center text-xl font-semibold">
            {profile.name?.[0] || "U"}
          </div>
          <div>
            <div className="text-lg font-semibold text-slate-900" data-testid="profile-name-display">{profile.name}</div>
            <div className="text-xs uppercase tracking-widest text-red-700 font-semibold" data-testid="profile-role-display">{roleLabel}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Name" testid="profile-name">
            {editing && isAdmin ? (
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-name" />
            ) : (
              <ReadValue>{profile.name}</ReadValue>
            )}
          </Field>

          <Field label="Employee ID" testid="profile-emp">
            {editing && isAdmin ? (
              <Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="input-employee-id" />
            ) : (
              <ReadValue>{profile.employee_id || "—"}</ReadValue>
            )}
          </Field>

          <Field label="Department" testid="profile-dept">
            {editing && isAdmin ? (
              <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="input-department" />
            ) : (
              <ReadValue>{profile.department || "—"}</ReadValue>
            )}
          </Field>

          <Field label="Designation" testid="profile-desig">
            {editing && isAdmin ? (
              <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="input-designation" />
            ) : (
              <ReadValue>{profile.designation || "—"}</ReadValue>
            )}
          </Field>

          <Field label="Role" testid="profile-role">
            <ReadValue>{roleLabel}</ReadValue>
          </Field>

          <Field label="Email" testid="profile-email">
            {editing ? (
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
            ) : (
              <ReadValue>{profile.email}</ReadValue>
            )}
          </Field>

          <Field label="Mobile Number" testid="profile-mobile">
            {editing ? (
              <Input value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} placeholder="+91 …" data-testid="input-mobile" />
            ) : (
              <ReadValue>{profile.mobile_number || "—"}</ReadValue>
            )}
          </Field>

          <Field label="Last Login" testid="profile-lastlogin">
            <ReadValue>
              {profile.last_login_at ? new Date(profile.last_login_at).toLocaleString() : "—"}
            </ReadValue>
          </Field>
        </div>

        {editing && (
          <div className="flex justify-end gap-2 pt-6 mt-4 border-t border-slate-100">
            <Button variant="outline" onClick={cancelEdit} disabled={saving} data-testid="btn-cancel-profile">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={saveProfile} disabled={saving} data-testid="btn-save-profile">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        )}
      </Card>

      <GmailSettingsCard />
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ReadValue({ children }) {
  return <div className="text-sm text-slate-900 py-2">{children}</div>;
}
